// チャット表示＋入力。メッセージ一覧と送信欄。
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { UseSecretaryResult } from '../hooks/useSecretary';
import type { WriteToolSuccess } from '../tools/types';

interface ChatViewProps {
  secretary: UseSecretaryResult;
}

/**
 * 書込の結果カード。LLM の文章ではなく、実際に書き込まれたレコード
 * （ツールの実行結果）から描画する ＝ 作られていなければカードは出ない。
 */
function WriteResultCard({ write }: { write: WriteToolSuccess }) {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate(write.url)}
      className="mt-1 flex w-full max-w-[85%] items-center gap-2 rounded-lg border border-yellow-300 bg-yellow-50 px-3 py-2 text-left text-sm text-yellow-900 transition hover:bg-yellow-100"
    >
      <span aria-hidden>🤖</span>
      <span className="flex-1">
        {write.label} <span className="font-semibold">{write.code}</span> を
        {write.kind === 'create' ? '作成' : '更新'}しました（AIレビュー待ち）
      </span>
      <span className="text-xs text-yellow-700 whitespace-nowrap">開いて確認 →</span>
    </button>
  );
}

export function ChatView({ secretary }: ChatViewProps) {
  const { messages, isSending, error, send } = secretary;
  const [text, setText] = useState('');

  const submit = () => {
    if (!text.trim() || isSending) return;
    void send(text);
    setText('');
  };

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="flex-1 space-y-2 overflow-auto p-3">
        {messages.map((m, i) => (
          <div key={i} className={m.role === 'user' ? 'text-right' : 'text-left'}>
            <span
              className={`inline-block max-w-[85%] whitespace-pre-wrap rounded-lg px-3 py-2 text-sm ${
                m.role === 'user' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-800'
              }`}
            >
              {m.content}
            </span>
            {m.writes?.map((write) => (
              <WriteResultCard key={`${write.kind}:${write.resource}:${write.id}`} write={write} />
            ))}
          </div>
        ))}
        {isSending && <p className="text-xs text-gray-400">考え中…</p>}
        {error && <p className="text-xs text-red-600">{error}</p>}
      </div>
      <div className="flex gap-2 border-t border-gray-200 p-2">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            // 送信は Shift+Enter のみ。Enter単独は改行（誤送信防止）。
            // isComposing: 日本語IMEの変換確定Enterで送信されないようにする。
            if (e.key === 'Enter' && e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              submit();
            }
          }}
          rows={2}
          placeholder="メッセージを入力（Shift+Enterで送信）"
          className="flex-1 resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
        <button
          type="button"
          onClick={submit}
          disabled={isSending}
          className="rounded-lg bg-blue-600 px-3 text-sm text-white transition hover:bg-blue-700 disabled:opacity-50"
        >
          送信
        </button>
      </div>
    </div>
  );
}
